import React, { useState, useEffect } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Hero: React.FC = () => {
  const [leftRef, leftVisible] = useScrollAnimation(0.2);
  const [rightRef, rightVisible] = useScrollAnimation(0.2);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentText, setCurrentText] = useState(0);

  const dynamicTexts = [
    "Marketing Excellence",
    "Brand Activations That Soar",
    "3D Billboards & Beyond",
    "Experiential Campaigns"
  ];

  useEffect(() => {
    setIsLoaded(true);
    
    // Text rotation effect
    const textInterval = setInterval(() => {
      setCurrentText((prev) => (prev + 1) % dynamicTexts.length);
    }, 4000);

    return () => {
      clearInterval(textInterval);
    };
  }, [dynamicTexts.length]);

  const stats = [
    { number: '500+', label: 'Campaigns', icon: '🚀' },
    { number: '50+', label: 'Clients', icon: '⭐' },
    { number: '15+', label: 'Years', icon: '🏆' },
    { number: '99%', label: 'Satisfaction', icon: '💯' }
  ];

  return (
    <section id="home" className="relative py-16 bg-gradient-to-br from-slate-900 via-orange-900 to-slate-900">
      {/* Simple Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-600/10 via-red-600/10 to-yellow-600/10"></div>
        <div className="absolute top-20 left-20 w-32 h-32 bg-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div 
            ref={leftRef}
            className={`text-center lg:text-left transition-all duration-1000 ${
              leftVisible 
                ? 'opacity-100 translate-x-0' 
                : 'opacity-0 -translate-x-10'
            }`}
          >
            {/* Badge */}
            <div className={`inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-white text-sm font-medium mb-6 ${
              isLoaded ? 'animate-fade-in-up' : ''
            }`}>
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              <span>Marketing Excellence</span>
            </div>

            {/* Main Heading */}
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 ${
              isLoaded ? 'animate-fade-in-up' : ''
            }`} style={{ animationDelay: '0.2s' }}>
              <span className="block">Elevating Brands,</span>
              <span className="block bg-gradient-to-r from-orange-400 via-red-400 to-yellow-400 bg-clip-text text-transparent">
                {dynamicTexts[currentText]}
              </span>
              <span className="block bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                That Soar Higher
              </span>
            </h1>
            
            {/* Description */}
            <p className={`text-lg text-gray-300 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0 ${
              isLoaded ? 'animate-fade-in-up' : ''
            }`} style={{ animationDelay: '0.4s' }}>
              Specialized marketing solutions for multinational brands. From concept designs to experiential campaigns, we help brands reach new heights and memorable experiences.
            </p>

            {/* CTA Buttons */}
            <div className={`flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8 ${
              isLoaded ? 'animate-fade-in-up' : ''
            }`} style={{ animationDelay: '0.6s' }}>
              <button 
                onClick={() => window.location.href = '/quote'}
                className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-orange-500/25 transition-all duration-300 hover:scale-105"
              >
                Start Your Campaign
              </button>
              <button 
                onClick={() => {
                  const portfolioSection = document.getElementById('portfolio');
                  if (portfolioSection) {
                    const headerHeight = 120;
                    const elementPosition = portfolioSection.offsetTop - headerHeight;
                    window.scrollTo({
                      top: elementPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
                className="border border-white/30 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 hover:border-orange-400/50 transition-all duration-300"
              >
                View Our Work
              </button>
            </div>

            {/* Stats */}
            <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 ${
              isLoaded ? 'animate-fade-in-up' : ''
            }`} style={{ animationDelay: '0.8s' }}>
              {stats.map((stat, index) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <div className="text-2xl font-bold text-white mb-1">{stat.number}</div>
                  <div className="text-sm text-gray-300 flex items-center justify-center lg:justify-start space-x-1">
                    <span>{stat.icon}</span>
                    <span>{stat.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content */}
          <div 
            ref={rightRef}
            className={`relative transition-all duration-1000 ${
              rightVisible 
                ? 'opacity-100 translate-x-0' 
                : 'opacity-0 translate-x-10'
            }`}
          >
              {/* Feature Card */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-white shadow-2xl">
              <div className="text-center mb-8">
                <h3 className="text-3xl sm:text-4xl font-bold mb-3">
                  <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                    Creative Excellence,
                  </span>
                  <br />
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                    Measurable Results
                  </span>
                </h3>
                <div className="w-20 h-1 bg-gradient-to-r from-orange-400 to-yellow-400 mx-auto rounded-full shadow-lg"></div>
              </div>

              {/* Enhanced Features */}
              <div className="space-y-5">
                <div className="flex items-center space-x-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="p-3 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-bold text-base text-white">ATL + BTL Mastery</div>
                    <div className="text-sm text-gray-300">Above & Below the Line Excellence</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="p-3 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-bold text-base text-white">360° Brand Activations</div>
                    <div className="text-sm text-gray-300">Complete Campaign Solutions</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="p-3 bg-gradient-to-r from-red-500 to-yellow-500 rounded-xl shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-bold text-base text-white">Proven with Top Brands</div>
                    <div className="text-sm text-gray-300">Trusted by Industry Leaders</div>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => window.location.href = '/quote'}
                className="w-full mt-8 bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:shadow-xl hover:shadow-orange-500/25 transition-all duration-300 hover:scale-105"
              >
                Start Your Journey
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
        <div className="flex flex-col items-center space-y-2">
          <span className="text-xs font-medium">Scroll to explore</span>
          <div className="w-5 h-8 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-2 bg-white/60 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Custom CSS */}
      <style>{`
        @keyframes fade-in-up {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        .animate-fade-in-up { 
          animation: fade-in-up 0.6s ease-out forwards; 
        }
      `}</style>
    </section>
  );
};

export default Hero;